import type {
  GradeColor,
  GradeWithCategories,
} from "@core/grades/types";
import type { BreadcrumbItem } from "@core/shared/types";
import { URLS } from "@core/shared/utils";
import { trans } from "@mongez/localization";

export type GradeDetailsTheme = {
  accent: string;
  badge: string;
  glow: string;
  gradient: string;
  soft: string;
};

export type GradeDetailsCategoryViewModel = {
  category: GradeWithCategories["categories"][number];
  index: number;
  lessonCount: number;
  sectionId: string;
};

const gradeThemes: Record<GradeColor, GradeDetailsTheme> = {
  red: {
    accent: "text-red-600 dark:text-red-300",
    badge: "bg-red-500 text-white",
    glow: "bg-red-300/30 dark:bg-red-500/10",
    gradient: "from-red-500 via-rose-500 to-orange-400",
    soft: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-200",
  },
  green: {
    accent: "text-emerald-600 dark:text-emerald-300",
    badge: "bg-emerald-500 text-white",
    glow: "bg-emerald-300/30 dark:bg-emerald-500/10",
    gradient: "from-emerald-500 via-teal-500 to-cyan-400",
    soft: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200",
  },
  blue: {
    accent: "text-blue-600 dark:text-blue-300",
    badge: "bg-blue-500 text-white",
    glow: "bg-blue-300/30 dark:bg-blue-500/10",
    gradient: "from-blue-600 via-indigo-500 to-cyan-400",
    soft: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200",
  },
  orange: {
    accent: "text-orange-600 dark:text-orange-300",
    badge: "bg-orange-500 text-white",
    glow: "bg-orange-300/30 dark:bg-orange-500/10",
    gradient: "from-orange-500 via-amber-500 to-yellow-400",
    soft: "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-200",
  },
  yellow: {
    accent: "text-amber-600 dark:text-amber-300",
    badge: "bg-amber-500 text-white",
    glow: "bg-yellow-300/30 dark:bg-yellow-500/10",
    gradient: "from-yellow-400 via-amber-500 to-orange-400",
    soft: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200",
  },
  purple: {
    accent: "text-violet-600 dark:text-violet-300",
    badge: "bg-violet-500 text-white",
    glow: "bg-violet-300/30 dark:bg-violet-500/10",
    gradient: "from-violet-600 via-purple-500 to-fuchsia-400",
    soft: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-200",
  },
  pink: {
    accent: "text-pink-600 dark:text-pink-300",
    badge: "bg-pink-500 text-white",
    glow: "bg-pink-300/30 dark:bg-pink-500/10",
    gradient: "from-pink-500 via-fuchsia-500 to-violet-400",
    soft: "bg-pink-50 text-pink-700 dark:bg-pink-950/40 dark:text-pink-200",
  },
  gray: {
    accent: "text-slate-600 dark:text-slate-300",
    badge: "bg-slate-600 text-white",
    glow: "bg-slate-300/30 dark:bg-slate-500/10",
    gradient: "from-slate-600 via-slate-500 to-zinc-400",
    soft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  },
  black: {
    accent: "text-slate-700 dark:text-slate-200",
    badge: "bg-slate-900 text-white",
    glow: "bg-slate-400/30 dark:bg-slate-500/10",
    gradient: "from-slate-950 via-slate-800 to-slate-600",
    soft: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100",
  },
  white: {
    accent: "text-slate-600 dark:text-slate-200",
    badge: "bg-slate-700 text-white",
    glow: "bg-white/60 dark:bg-white/5",
    gradient: "from-slate-500 via-slate-400 to-slate-300",
    soft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  },
  teal: {
    accent: "text-teal-600 dark:text-teal-300",
    badge: "bg-teal-500 text-white",
    glow: "bg-teal-300/30 dark:bg-teal-500/10",
    gradient: "from-teal-600 via-cyan-500 to-sky-400",
    soft: "bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-200",
  },
  lime: {
    accent: "text-lime-700 dark:text-lime-300",
    badge: "bg-lime-600 text-white",
    glow: "bg-lime-300/30 dark:bg-lime-500/10",
    gradient: "from-lime-500 via-green-500 to-emerald-400",
    soft: "bg-lime-50 text-lime-700 dark:bg-lime-950/40 dark:text-lime-200",
  },
  indigo: {
    accent: "text-indigo-600 dark:text-indigo-300",
    badge: "bg-indigo-500 text-white",
    glow: "bg-indigo-300/30 dark:bg-indigo-500/10",
    gradient: "from-indigo-600 via-blue-500 to-violet-400",
    soft: "bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200",
  },
  peach: {
    accent: "text-orange-600 dark:text-orange-300",
    badge: "bg-orange-400 text-white",
    glow: "bg-orange-200/40 dark:bg-orange-500/10",
    gradient: "from-orange-300 via-rose-300 to-pink-300",
    soft: "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-200",
  },
  coral: {
    accent: "text-rose-600 dark:text-rose-300",
    badge: "bg-rose-500 text-white",
    glow: "bg-rose-300/30 dark:bg-rose-500/10",
    gradient: "from-rose-500 via-orange-400 to-amber-300",
    soft: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200",
  },
  maroon: {
    accent: "text-red-800 dark:text-red-300",
    badge: "bg-red-800 text-white",
    glow: "bg-red-400/20 dark:bg-red-500/10",
    gradient: "from-red-950 via-red-800 to-rose-600",
    soft: "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200",
  },
  violet: {
    accent: "text-violet-600 dark:text-violet-300",
    badge: "bg-violet-500 text-white",
    glow: "bg-violet-300/30 dark:bg-violet-500/10",
    gradient: "from-violet-600 via-fuchsia-500 to-pink-400",
    soft: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-200",
  },
  brown: {
    accent: "text-amber-800 dark:text-amber-300",
    badge: "bg-amber-800 text-white",
    glow: "bg-amber-300/30 dark:bg-amber-500/10",
    gradient: "from-amber-900 via-amber-700 to-orange-500",
    soft: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  },
};

const defaultTheme = gradeThemes.indigo;

export function useGradeDetailsPage(grade: GradeWithCategories) {
  const categories: GradeDetailsCategoryViewModel[] = (
    grade.categories || []
  )
    .filter(category => category.lessons?.length)
    .map((category, index) => ({
      category,
      index: index + 1,
      lessonCount: category.total?.lessons || category.lessons.length,
      sectionId: `grade-category-${category.id}`,
    }));

  const totalLessons = categories.reduce(
    (total, item) => total + item.lessonCount,
    0,
  );

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: trans("grades.title"),
      url: URLS.grades.list,
    },
    {
      text: grade.name,
      url: URLS.grades.view(grade),
    },
  ];

  return {
    breadcrumbs,
    categories,
    hasCategories: categories.length > 0,
    theme: gradeThemes[grade.color] || defaultTheme,
    totalCategories: categories.length,
    totalLessons,
  };
}

